import React, { useState, useCallback } from "react";
import { Box, Flex } from "@chakra-ui/react";
import Card from "horizon/components/card/Card";
import { ListHeader, SearchBar, ListFooter } from "./shared/CertificationListParts";
import { CertificationFilters, ListPagination } from "./shared/CertificationListFilters";
import { CertificationTable } from "./shared/CertificationTableComponents";

/**
 * Executes a refresh of the certification list with loading state management.
 * @param {Function} setRefreshing - Refreshing state setter
 * @param {Function} refreshCertifications - API refresh callback
 * @returns {Promise<void>}
 */
async function executeRefresh(setRefreshing, refreshCertifications) {
    setRefreshing(true);
    try {
        await refreshCertifications();
    } catch (error) {
        console.error("Error refreshing certifications:", error);
    } finally {
        setRefreshing(false);
    }
}

/**
 * Renders the full-width certification list UI: header, search, collapsible
 * filters, table, pagination, and a compact total footer.
 * @param {Object} props - All display props for the list
 * @returns {React.ReactElement}
 */
function CertificationListContent(props) {
    const { refreshing, handleRefresh, isLoading, certifications, selectedCertification,
        onRowClick, filters, setFilters, pagination, page, setPage } = props;
    return (
        <Card>
            <Box>
                <ListHeader refreshing={refreshing} onRefresh={handleRefresh} />
                <Flex gap="10px" align="center" mb="32px">
                    <CertificationFilters status={filters.status} entityType={filters.entityType}
                        onStatusChange={(v) => setFilters({ status: v })}
                        onEntityTypeChange={(v) => setFilters({ entityType: v })}
                        onClear={() => setFilters({ status: "all", entityType: "all" })} />
                    <Box flex="1" minW="0">
                        <SearchBar searchTerm={filters.search} onSearchChange={(v) => setFilters({ search: v })} />
                    </Box>
                </Flex>
                <CertificationTable isLoading={isLoading} filteredCertifications={certifications}
                    selectedCertification={selectedCertification} onSelect={onRowClick} />
                <ListPagination page={page} pages={pagination.pages} onPageChange={setPage} />
                <ListFooter count={pagination.total} />
            </Box>
        </Card>
    );
}

/**
 * Displays the filterable, paginated list of certification requests in full
 * width. Selecting a row opens the detail drawer (owned by the parent).
 * State is owned by the parent (certifications.js); this component is presentational.
 * @param {Object} props - Component props
 * @param {Array} props.certifications - Current page of certifications
 * @param {Object|null} props.selectedCertification - Currently highlighted certification
 * @param {Function} props.onRowClick - Callback to open the detail drawer for a row
 * @param {boolean} props.loading - Whether the initial data is loading
 * @param {Function} props.refreshCertifications - Callback to refresh the current page
 * @param {Object} props.filters - { status, entityType, search }
 * @param {Function} props.setFilters - Partial filter setter (resets page to 1)
 * @param {Object} props.pagination - { page, pages, total }
 * @param {number} props.page - Current page (1-indexed)
 * @param {Function} props.setPage - Page setter
 * @returns {React.ReactElement}
 */
function CertificationList(props) {
    const { refreshCertifications } = props;
    const [refreshing, setRefreshing] = useState(false);
    const handleRefresh = useCallback(() => executeRefresh(setRefreshing, refreshCertifications), [refreshCertifications]);
    const isLoading = props.loading || refreshing;
    return <CertificationListContent refreshing={refreshing} handleRefresh={handleRefresh} isLoading={isLoading} {...props} />;
}

export default CertificationList;
